import { DataProvider } from 'react-admin';
import { httpClient } from '@/utils/httpClient';
import type { DataTablesResponse } from '@/types';
import { INT_TO_USER_ROLE } from '@/config/constants';

/** Convert user role from backend int to frontend string */
const normalizeUserRole = (record: any) => {
  if (record && typeof record.role === 'number') {
    record.role = INT_TO_USER_ROLE[record.role] || 'User';
  }
  return record;
};

/**
 * KYC API resource configuration.
 * Maps React Admin resource names to API endpoint patterns.
 */
interface ResourceConfig {
  list: string;
  getOne: (id: any) => string;
  create: string;
  update: (id: any) => string;
  delete: (id: any) => string;
  idField?: string;
  /** If true, update endpoint doesn't include ID in URL */
  updateWithoutId?: boolean;
}

const resources: Record<string, ResourceConfig> = {
  accounts: {
    list: '/admin/account/GetAccounts',
    getOne: (id) => `/admin/account/GetAccountById/${id}`,
    create: '/admin/account/AddAccount',
    update: (id) => `/admin/account/UpdateAccount/${id}`,
    delete: (id) => `/admin/account/DeleteAccount/${id}`,
  },
  servers: {
    list: '/admin/server/GetServers',
    getOne: (id) => `/admin/server/GetServerById/${id}`,
    create: '/admin/server/AddServer',
    update: () => '/admin/server/UpdateServer',
    delete: (id) => `/admin/server/DeleteServer/${id}`,
    updateWithoutId: true,
  },
  licenses: {
    list: '/admin/license/GetLicenses',
    getOne: (id) => `/admin/license/GetLicenseById/${id}`,
    create: '/admin/license/AddLicense',
    update: () => '/admin/license/UpdateLicense',
    delete: (id) => `/admin/license/DeleteLicense/${id}`,
    updateWithoutId: true,
  },
  'document-types': {
    list: '/admin/document-type/GetDocumentTypes',
    getOne: (id) => `/admin/document-type/GetDocumentTypeById/${id}`,
    create: '/admin/document-type/AddDocumentType',
    update: () => '/admin/document-type/UpdateDocumentType',
    delete: (id) => `/admin/document-type/DeleteDocumentType/${id}`,
    updateWithoutId: true,
  },
  nationalities: {
    list: '/admin/nationality/GetNationality',
    getOne: (id) => `/admin/nationality/GetNationalityById/${id}`,
    create: '/admin/nationality/AddNationality',
    update: () => '/admin/nationality/UpdateNationality',
    delete: (id) => `/admin/nationality/DeleteNationality/${id}`,
    updateWithoutId: true,
  },
  'kyc-fields': {
    list: '/admin/kyc-field/GetKYCFields',
    getOne: (id) => `/admin/kyc-field/GetKYCFieldById/${id}`,
    create: '/admin/kyc-field/AddKYCField',
    update: () => '/admin/kyc-field/UpdateKYCField',
    delete: (id) => `/admin/kyc-field/DeleteKYCField/${id}`,
    updateWithoutId: true,
  },
  users: {
    list: '/admin/user',
    getOne: (id) => `/admin/user/${id}`,
    create: '/admin/user',
    update: () => '/admin/user',
    delete: (id) => `/admin/user/${id}`,
    updateWithoutId: true,
  },
  'fields-mapping': {
    list: '/admin/fields-mapping/GetMappingFields',
    getOne: (id) => `/admin/fields-mapping/GetFieldMappingById/${id}`,
    create: '/admin/fields-mapping/AddFieldMapping',
    update: () => '/admin/fields-mapping/UpdateFieldMapping',
    delete: (id) => `/admin/fields-mapping/DeleteFieldMapping/${id}`,
    updateWithoutId: true,
  },
  'default-mapping': {
    list: '/admin/default-mapping/GetDefaultMappings',
    getOne: (id) => `/admin/default-mapping/GetDefaultMappingById/${id}`,
    create: '/admin/default-mapping/AddDefaultMapping',
    update: () => '/admin/default-mapping/UpdateDefaultMapping',
    delete: (id) => `/admin/default-mapping/DeleteDefaultMapping/${id}`,
    updateWithoutId: true,
  },
  statistics: {
    list: '/admin/account/GetAccountStatistics',
    getOne: () => '',
    create: '',
    update: () => '',
    delete: () => '',
  },
  'cost-centers': {
    list: '/admin/cost-center/GetCostCenters',
    getOne: (id) => `/admin/cost-center/GetCostCenterById/${id}`,
    create: '/admin/cost-center/AddCostCenter',
    update: () => '/admin/cost-center/UpdateCostCenter',
    delete: (id) => `/admin/cost-center/DeleteCostCenter/${id}`,
    updateWithoutId: true,
  },
};

/** Column mapping for server-side sorting */
const columnMappings: Record<string, Record<string, string>> = {
  accounts: {
    'accountResponse.accountName': 'accountName',
    accountName: 'accountName',
    id: 'accountName',
    'accountResponse.accountStatus': 'status',
    'licenseResponse.licenseName': 'licenseName',
    validityTimeFrame: 'validityTimeFrame',
    'accountResponse.rateLimitApplies': 'rateLimitApplies',
    'accountResponse.loggingStatus': 'loggingStatus',
    'accountResponse.primaryServer.name': 'accountName',
  },
  statistics: {
    accountName: 'accountName',
  },
  users: {
    name: 'name',
    email: 'email',
  },
  'document-types': { name: 'name' },
  nationalities: { name: 'name' },
  'kyc-fields': { name: 'name' },
  licenses: { licenseName: 'licenseName' },
  servers: { name: 'name', port: 'port', url: 'url', status: 'status' },
  'fields-mapping': { documentType: 'documentType', documentSide: 'documentSide', nationality: 'nationality' },
  'default-mapping': { standardFieldName: 'standardFieldName', priority: 'priority' },
  'cost-centers': { name: 'name' },
};

const getConfig = (resource: string): ResourceConfig => {
  const config = resources[resource];
  if (!config) {
    throw new Error(`Unknown resource: ${resource}`);
  }
  return config;
};

/**
 * Build query params in DataTables format for the KYC API.
 */
const buildDTParams = (
  resource: string,
  pagination: { page: number; perPage: number },
  sort: { field: string; order: string },
  filter: Record<string, any>
): string => {
  const params = new URLSearchParams();
  params.set('draw', '1');
  params.set('length', String(pagination.perPage));
  params.set('start', String((pagination.page - 1) * pagination.perPage));
  params.set('search', filter?.q || filter?.search || '');

  const mapping = columnMappings[resource];
  const column = mapping?.[sort.field] || sort.field || '';
  params.set('column', column);
  params.set('orderBy', sort.order === 'ASC' ? 'asc' : 'desc');

  // Pass additional filters as query params (backend-supported)
  const skip = ['q', 'search', '__dateMode'];
  for (const [key, value] of Object.entries(filter || {})) {
    if (!skip.includes(key) && value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }

  return params.toString();
};

/**
 * Ensure every record has an `id` field for React Admin.
 */
const ensureId = (record: any, resource: string, index?: number): any => {
  if (record.id !== undefined) return record;
  // Account resource nests the id
  if (resource === 'accounts' && record.accountResponse?.id) {
    return { ...record, id: record.accountResponse.id };
  }
  // Statistics items have no id; use accountName as synthetic id
  if (resource === 'statistics') {
    return { ...record, id: record.accountName || index };
  }
  return record;
};

export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    const config = getConfig(resource);
    const query = buildDTParams(resource, params.pagination, params.sort, params.filter || {});
    const url = `${config.list}?${query}`;
    const response = await httpClient.get<any>(url);
    const json = response.data;

    // DataTables format
    if (json.data && json.recordsTotal !== undefined) {
      const dtResponse = json as DataTablesResponse<any>;
      return {
        data: dtResponse.data.map((r: any, i: number) => ensureId(r, resource, i)),
        total: dtResponse.recordsFiltered ?? dtResponse.recordsTotal,
      };
    }

    // Array response
    if (Array.isArray(json)) {
      return {
        data: json.map((r: any, i: number) => ensureId(r, resource, i)),
        total: json.length,
      };
    }

    return { data: [], total: 0 };
  },

  getOne: async (resource, params) => {
    const config = getConfig(resource);
    const url = config.getOne(params.id);
    const response = await httpClient.get<any>(url);
    let record = ensureId(response.data, resource);
    if (resource === 'users') record = normalizeUserRole(record);
    return { data: record };
  },

  getMany: async (resource, params) => {
    const results = await Promise.all(
      params.ids.map(async (id) => {
        const config = getConfig(resource);
        const response = await httpClient.get<any>(config.getOne(id));
        return ensureId(response.data, resource);
      })
    );
    return { data: results };
  },

  getManyReference: async (resource, params) => {
    return { data: [], total: 0 };
  },

  create: async (resource, params) => {
    const config = getConfig(resource);
    const response = await httpClient.post<any>(config.create, params.data);
    let record = ensureId(response.data || { ...params.data, id: 0 }, resource);
    if (resource === 'users') record = normalizeUserRole(record);
    return { data: record };
  },

  update: async (resource, params) => {
    const config = getConfig(resource);
    const url = config.update(params.id);
    const response = await httpClient.put<any>(url, params.data);
    // Backend may return 204 No Content — ensure we always return a valid record with id
    const data = (response.data && Object.keys(response.data).length > 0)
      ? response.data
      : { ...params.previousData, ...params.data };
    return { data: { ...data, id: params.id } };
  },

  updateMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) => {
        const config = getConfig(resource);
        return httpClient.put(config.update(id), params.data);
      })
    );
    return { data: params.ids };
  },

  delete: async (resource, params) => {
    const config = getConfig(resource);
    await httpClient.delete(config.delete(params.id));
    return { data: params.previousData as any };
  },

  deleteMany: async (resource, params) => {
    const config = getConfig(resource);
    await Promise.all(params.ids.map((id) => httpClient.delete(config.delete(id))));
    return { data: params.ids };
  },
};

export default dataProvider;
